import React from 'react';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Paper,
    Stack,
    Tooltip,
    Typography,
    CircularProgress,
} from '@mui/material';
import {
    Cancel as CancelIcon,
    CheckCircle as CheckCircleIcon,
    Close as CloseIcon,
    HelpOutline as HelpOutlineIcon,
    Search as SearchIcon,
    Add as AddIcon,
    DeleteOutline as DeleteOutlineIcon,
} from '@mui/icons-material';
import {useReportStore} from '../../../../features/products/useReportStore';
import useSourcePackageBuilderStore from '../../../../stores/useSourcePackageBuilderStore';
import useSourcesExplorerStore from '../../../../features/products/reportEditorPage/leftPanel/components/useSourcesExplorerStore';
import {normalizeKeywordsQuery} from './utils/normalizeKeywordsQuery';
import {getScoreColor, getScoreLabel} from "../../../../utils/claimAnalysisApi";
import {buildCitationFromEvidence, findAddedReference, isCitationAlreadyAdded} from './utils/buildCitationFromEvidence';
import {buildSupportingEvidenceRows} from './utils/buildSupportingEvidenceRows';
import {buildSuggestedEvidenceRows} from './utils/buildSuggestedEvidenceRows';
import {buildContradictionEvidenceGroups} from './utils/buildContradictionEvidenceRows';
import {getSupportingEvidenceCount} from './utils/getSupportingEvidenceCount';
import {getClaimSuggestedCount} from './utils/getClaimBadgeCounts';
import {
    getContradictionsForClaim,
    getExtrinsicNoteForClaim,
} from './utils/claimProofAlerts';
import ClaimExtrinsicAlert from './ClaimExtrinsicAlert';

/**
 * Claim Details Dialog Component
 * Displays comprehensive details about a single claim including supporting and contradicting evidence
 *
 * @param {Object} props
 * @param {boolean} props.open - Dialog open state
 * @param {Function} props.onClose - Close handler
 * @param {Object} props.claim - Claim object
 * @param {Array} props.references - Full references array from marker
 * @param {Object} props.evidenceScores - Evidence scores object keyed by referenceId
 * @param {Function} [props.onAddEvidence] - Add supporting evidence as a reference
 * @param {Function} [props.onRemoveEvidence] - Remove an added reference by localId
 * @param {boolean} [props.verificationLoading] - True while verification/discovery is in progress
 * @param {Array} [props.suggestedReferences] - V4 backend suggestions for the mark
 * @param {object[]} [props.contradictions] - V4 contradiction groups for the mark
 * @param {Record<string, string>} [props.extrinsicNotes] - V4 extrinsic notes by claimId
 * @param {boolean} [props.insightsLoading] - V4 suggest pipeline still running
 * @param {Array} [props.perClaimCitations] - Deprecated client search hits (ignored when backend present)
 */
export default function ClaimDetailsDialog({
    open,
    onClose,
    claim,
    references,
    evidenceScores,
    onAddEvidence,
    onRemoveEvidence,
    verificationLoading = false,
    suggestedReferences = [],
    contradictions = [],
    extrinsicNotes = {},
    insightsLoading = false,
    perClaimCitations = [],
}) {
    const setLeftPanelTab = useReportStore(state => state.setLeftPanelTab);
    const setUnifiedSearchMode = useReportStore(state => state.setUnifiedSearchMode);
    const setInputValue = useSourcesExplorerStore(state => state.setInputValue);
    const openSourcePackageBuilder = useSourcePackageBuilderStore(s => s.open);

    // Defensive: ensure we always deal with an array
    const safeReferences = React.useMemo(
        () => Array.isArray(references) ? references : [],
        [references]
    );

    const getReference = React.useCallback((referenceId) => {
        if (!referenceId) return null;
        // Match by localId (ymap) first, fall back to firestoreId (legacy Firestore)
        return safeReferences.find(r => r.localId === referenceId || r.firestoreId === referenceId) || null;
    }, [safeReferences]);

    const getReferenceLabel = React.useCallback((ref, referenceId) => {
        if (!ref) return `Missing reference ${referenceId}`;
        return ref.docTitle || ref.title || ref.name || `Reference ${referenceId}`;
    }, []);

    const getEvidenceScore = React.useCallback((referenceId) => {
        if (!referenceId) return null;
        // Check if evidence score is directly on the reference object
        const reference = getReference(referenceId);
        if (reference?.evidenceScore != null) {
            // New flat structure: evidenceScore is a number, evidenceScoreReason is a string
            if (typeof reference.evidenceScore === 'object') {
                return reference.evidenceScore;
            }
            return {score: reference.evidenceScore, reason: reference.evidenceScoreReason || null};
        }
        // Fall back to evidenceScores prop
        if (evidenceScores && evidenceScores[referenceId]) {
            return evidenceScores[referenceId];
        }
        return null;
    }, [evidenceScores, getReference]);

    const handleDialogClose = React.useCallback(() => {
        onClose?.();
    }, [onClose]);

    const handleViewReference = React.useCallback((event, referenceId, corpusEntry = null) => {
        event?.preventDefault?.();
        event?.stopPropagation?.();

        const reference = referenceId ? getReference(referenceId) : null;

        let documentId = reference?.docId ?? null;
        let segmentIndex = reference?.index ?? reference?.segmentIndex ?? null;

        if (!documentId && corpusEntry) {
            documentId = corpusEntry.docId ?? corpusEntry.dataItemReferenceId ?? corpusEntry.canonicalFileId ?? null;
            segmentIndex = corpusEntry.segmentIndex ?? corpusEntry.segmentId ?? null;
        }

        if (!documentId && referenceId && typeof referenceId === 'string') {
            const corpusMatch = referenceId.match(/^(\d+):(\d+)$/);
            if (corpusMatch) {
                documentId = Number(corpusMatch[1]);
                segmentIndex = Number(corpusMatch[2]);
            }
            const dataMatch = referenceId.match(/^data:(\d+):(\d+)$/);
            if (dataMatch) {
                documentId = Number(dataMatch[1]);
                segmentIndex = Number(dataMatch[2]);
            }
        }

        if (!documentId) {
            console.warn('[ClaimDetailsDialog] Missing document id for evidence', referenceId, corpusEntry);
            return;
        }

        try {
            openSourcePackageBuilder({
                documentId,
                segmentIndex: segmentIndex ?? null,
            });
        } catch (error) {
            console.error('Failed to open document:', error);
        }
    }, [getReference, openSourcePackageBuilder]);

    const handleAddEvidence = React.useCallback((event, reference, corpusEntry, referenceId) => {
        event?.preventDefault?.();
        event?.stopPropagation?.();
        if (!onAddEvidence) return;

        const citation = buildCitationFromEvidence({reference, corpusEntry, referenceId});
        if (!citation) {
            console.warn('[ClaimDetailsDialog] Cannot build citation for evidence', referenceId, corpusEntry);
            return;
        }
        onAddEvidence(citation);
    }, [onAddEvidence]);

    const handleRemoveEvidence = React.useCallback((event, citation) => {
        event?.preventDefault?.();
        event?.stopPropagation?.();
        if (!onRemoveEvidence) return;

        const added = findAddedReference(citation, safeReferences);
        const removeId = added?.localId || added?.firestoreId;
        if (!removeId) {
            console.warn('[ClaimDetailsDialog] Cannot remove evidence — not in added references', citation);
            return;
        }
        onRemoveEvidence(removeId);
    }, [onRemoveEvidence, safeReferences]);

    const handleSearchForMore = React.useCallback(() => {
        const booleanQuery = normalizeKeywordsQuery(claim?.keywords_query);
        const semanticQuery = claim?.semantic_query?.trim() || claim?.claim?.trim();

        if (booleanQuery) {
            setInputValue(booleanQuery);
            setUnifiedSearchMode('keyword');
            setLeftPanelTab('search');
        } else if (semanticQuery) {
            setInputValue(semanticQuery);
            setUnifiedSearchMode('ai');
            setLeftPanelTab('search');
        }
        onClose?.();
    }, [claim?.keywords_query, claim?.semantic_query, claim?.claim, onClose, setUnifiedSearchMode, setLeftPanelTab, setInputValue]);

    const getStatusIcon = React.useCallback((status) => {
        switch (status) {
            case 'proven':
                return <CheckCircleIcon/>;
            case 'not_proven':
                return <HelpOutlineIcon/>;
            case 'contradicted':
                return <CancelIcon/>;
            default:
                return null;
        }
    }, []);

    const getStatusColor = React.useCallback((status) => {
        switch (status) {
            case 'proven':
                return 'success';
            case 'not_proven':
                return 'warning';
            case 'contradicted':
                return 'error';
            default:
                return 'default';
        }
    }, []);

    const getImportanceColor = React.useCallback((importance) => {
        switch (importance) {
            case 'high':
                return 'error';
            case 'medium':
                return 'warning';
            case 'low':
                return 'default';
            default:
                return 'default';
        }
    }, []);

    const supportingEvidenceRows = React.useMemo(
        () => buildSupportingEvidenceRows(claim, safeReferences)
            .filter((row) => findAddedReference(row.citation, safeReferences)),
        [claim, safeReferences]
    );
    const suggestedEvidenceRows = React.useMemo(
        () => buildSuggestedEvidenceRows(claim, safeReferences, suggestedReferences),
        [claim, safeReferences, suggestedReferences]
    );
    const supportingEvidenceCount = getSupportingEvidenceCount(claim, safeReferences);
    const suggestedEvidenceCount = getClaimSuggestedCount(claim, safeReferences, suggestedReferences);
    const isLoadingSupportingEvidence = verificationLoading && supportingEvidenceCount === 0 && Boolean(claim);

    const extrinsicNote = React.useMemo(
        () => (claim ? getExtrinsicNoteForClaim(extrinsicNotes, claim.id) : null),
        [claim, extrinsicNotes]
    );
    const claimContradictions = React.useMemo(
        () => (claim ? getContradictionsForClaim(contradictions, claim.id) : []),
        [claim, contradictions]
    );
    const contradictionEvidenceGroups = React.useMemo(
        () => buildContradictionEvidenceGroups(claimContradictions),
        [claimContradictions]
    );

    const renderEvidenceList = React.useCallback((rows, section) => (
        <Paper variant="outlined">
            <List disablePadding>
                {rows.map((row, idx) => {
                    const {referenceId, reference, corpusEntry, citation} = row;
                    const evidenceScore = getEvidenceScore(referenceId);

                    const label = reference
                        ? getReferenceLabel(reference, referenceId)
                        : (corpusEntry?.docTitle
                            || citation?.docTitle
                            || (typeof referenceId === 'string' && /^(?:\d+:\d+|data:\d+:\d+)$/.test(referenceId)
                                ? 'Corpus passage'
                                : referenceId ? `Missing reference ${referenceId}` : 'Corpus passage'));
                    const snippetText = reference?.content || corpusEntry?.text || citation?.content || null;
                    const canOpenSource = Boolean(
                        reference?.docId
                        || corpusEntry?.docId
                        || corpusEntry?.dataItemReferenceId
                        || corpusEntry?.canonicalFileId
                        || citation?.docId
                        || (typeof referenceId === 'string' && /^(?:\d+:\d+|data:\d+:\d+)$/.test(referenceId))
                    );
                    const isAdded = isCitationAlreadyAdded(citation, safeReferences);
                    const addedReference = findAddedReference(citation, safeReferences);
                    const canAdd = Boolean(onAddEvidence && citation && !isAdded);
                    const canRemove = Boolean(onRemoveEvidence && addedReference);

                    return (<React.Fragment key={`${claim.id}-${section}-${row.referenceId || idx}`}>
                        {idx > 0 && <Divider/>}
                        <ListItem
                            disablePadding
                            data-testid={`claim-details-${section}-item-${idx}`}
                            sx={{flexDirection: 'column', alignItems: 'stretch'}}
                        >
                            <Box sx={{display: 'flex', width: '100%', alignItems: 'stretch'}}>
                                <ListItemButton
                                    onClick={(e) => handleViewReference(e, referenceId, corpusEntry)}
                                    disabled={!canOpenSource}
                                    sx={{flex: 1}}
                                >
                                    <ListItemText
                                        primary={<Stack spacing={1}>
                                            <Stack direction="row" spacing={1}
                                                   alignItems="center">
                                                <Typography variant="body1"
                                                            fontWeight={600}
                                                            sx={{
                                                                flex: 1,
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                            }}
                                                >
                                                    {label}
                                                </Typography>

                                                {section === 'suggested' && row.claimCoverage != null && (
                                                    <Chip
                                                        label={`Coverage ${(row.claimCoverage * 100).toFixed(0)}%`}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{height: 20, fontSize: '0.7rem'}}
                                                    />
                                                )}

                                                {section === 'contradicting' && row.segmentScore != null && (
                                                    <Chip
                                                        label={`Score ${(row.segmentScore * 100).toFixed(0)}%`}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{height: 20, fontSize: '0.7rem'}}
                                                    />
                                                )}

                                                {evidenceScore && evidenceScore.score != null && (
                                                    <Tooltip
                                                        title={evidenceScore.reason || 'Evidence score'}
                                                        arrow>
                                                        <Chip
                                                            label={getScoreLabel(evidenceScore.score)}
                                                            size="small"
                                                            sx={{
                                                                height: 20,
                                                                fontSize: '0.7rem',
                                                                fontWeight: 600,
                                                                bgcolor: getScoreColor(evidenceScore.score),
                                                                color: 'white',
                                                                '&:hover': {
                                                                    bgcolor: getScoreColor(evidenceScore.score),
                                                                },
                                                            }}
                                                        />
                                                    </Tooltip>)}
                                            </Stack>

                                            {snippetText && (
                                                <Typography variant="body2"
                                                            color="text.secondary">
                                                    "{snippetText}"
                                                </Typography>)}
                                        </Stack>}
                                    />
                                </ListItemButton>
                                {(onAddEvidence || onRemoveEvidence) && citation && (
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        px: 1,
                                        flexShrink: 0,
                                    }}>
                                        {canRemove ? (
                                            <Tooltip title="Remove from references" arrow>
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => handleRemoveEvidence(e, citation)}
                                                    data-testid={`claim-details-${section}-remove-${idx}`}
                                                    sx={{
                                                        width: 32,
                                                        height: 32,
                                                        color: 'text.secondary',
                                                        '&:hover': {
                                                            bgcolor: 'rgba(0, 0, 0, 0.06)',
                                                            color: 'error.main',
                                                        },
                                                    }}
                                                >
                                                    <DeleteOutlineIcon fontSize="small"/>
                                                </IconButton>
                                            </Tooltip>
                                        ) : (
                                            <Tooltip title={isAdded ? 'Already added' : 'Add to references'} arrow>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            if (citation) {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                onAddEvidence?.(citation);
                                                                return;
                                                            }
                                                            handleAddEvidence(e, reference, corpusEntry, referenceId);
                                                        }}
                                                        disabled={!canAdd}
                                                        data-testid={`claim-details-${section}-add-${idx}`}
                                                        sx={{
                                                            width: 32,
                                                            height: 32,
                                                            color: 'success.main',
                                                            bgcolor: 'transparent',
                                                            '&:hover': {
                                                                bgcolor: 'rgba(46, 125, 50, 0.08)',
                                                            },
                                                        }}
                                                    >
                                                        <AddIcon fontSize="small"/>
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        </ListItem>
                    </React.Fragment>);
                })}
            </List>
        </Paper>
    ), [
        claim?.id,
        getEvidenceScore,
        getReferenceLabel,
        handleAddEvidence,
        handleRemoveEvidence,
        handleViewReference,
        onAddEvidence,
        onRemoveEvidence,
        safeReferences,
    ]);

    const renderContradictionEvidenceGroups = React.useCallback((groups) => (
        <Stack spacing={1.5}>
            {groups.map((group, groupIdx) => {
                const score = group.contradictionScore;
                const segmentCount = group.segments.length;

                return (
                    <Box
                        key={String(group.id ?? groupIdx)}
                        data-testid={`claim-details-contradicting-group-${groupIdx}`}
                        sx={{
                            border: '1px solid',
                            borderColor: 'error.light',
                            borderRadius: 1,
                            bgcolor: 'error.50',
                            p: 1.5,
                        }}
                    >
                        <Stack spacing={0.5}>
                            {group.comment ? (
                                <Typography variant="body2" sx={{fontWeight: 600, color: 'error.dark'}}>
                                    {group.comment}
                                </Typography>
                            ) : null}
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                {score != null && (
                                    <Chip
                                        label={`Score ${(score * 100).toFixed(0)}%`}
                                        size="small"
                                        variant="outlined"
                                        sx={{height: 20, fontSize: '0.7rem'}}
                                    />
                                )}
                                <Typography variant="caption" sx={{color: 'error.main'}}>
                                    {segmentCount} segment{segmentCount === 1 ? '' : 's'}
                                </Typography>
                            </Stack>
                        </Stack>
                        <Box sx={{mt: 1}}>
                            {renderEvidenceList(group.segments, 'contradicting')}
                        </Box>
                    </Box>
                );
            })}
        </Stack>
    ), [renderEvidenceList]);

    return (<Dialog
        open={open && Boolean(claim)}
        keepMounted
        disablePortal={false}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        aria-labelledby="claim-details-title"
        data-testid="claim-details-dialog"
        slotProps={{
            paper: {
                onMouseDown: (e) => e.stopPropagation(),
                onClick: (e) => e.stopPropagation(),
                'data-testid': 'claim-details-dialog-paper',
            }, backdrop: {
                onMouseDown: (e) => e.stopPropagation(),
                onClick: (e) => e.stopPropagation(),
                'data-testid': 'claim-details-dialog-backdrop',
            }
        }}
    >
        {/* Header */}
        <DialogTitle id="claim-details-title" data-testid="claim-details-title">
            Claim Details
            <IconButton
                aria-label="close"
                onClick={handleDialogClose}
                data-testid="claim-details-close"
                sx={{
                    position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon/>
            </IconButton>
        </DialogTitle>

        {/* Content */}
        <DialogContent
            dividers
            data-testid="claim-details-content"
        >
            {claim && (
            <Stack spacing={3}>
                {insightsLoading && !extrinsicNote && (
                    <Box
                        data-testid="claim-details-extrinsic-loading"
                        sx={{display: 'flex', alignItems: 'center', gap: 1}}
                    >
                        <CircularProgress size={14} thickness={4} />
                        <Typography variant="caption" sx={{color: 'text.secondary'}}>
                            Checking extrinsic corrections...
                        </Typography>
                    </Box>
                )}
                {extrinsicNote && (
                    <Box data-testid="claim-details-extrinsic">
                        <ClaimExtrinsicAlert note={extrinsicNote} />
                    </Box>
                )}

                {/* Claim Section */}
                <Paper variant="outlined" sx={{p: 2}} data-testid="claim-details-claim">
                    <Typography variant="h6" gutterBottom>
                        {claim.claim}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{mt: 1}}>
                        <Chip
                            icon={getStatusIcon(claim.status)}
                            label={(claim.status || '').replace('_', ' ').toUpperCase()}
                            color={getStatusColor(claim.status)}
                            size="small"
                            data-testid="claim-details-status"
                        />
                        <Chip
                            label={(claim.importance || '').toUpperCase()}
                            color={getImportanceColor(claim.importance)}
                            size="small"
                            data-testid="claim-details-importance"
                        />
                    </Stack>
                </Paper>

                {insightsLoading && claimContradictions.length === 0 && (
                    <Box
                        data-testid="claim-details-contradicting-loading"
                        sx={{display: 'flex', alignItems: 'center', gap: 1}}
                    >
                        <CircularProgress size={14} thickness={4} />
                        <Typography variant="caption" sx={{color: 'text.secondary'}}>
                            Checking contradicting evidence...
                        </Typography>
                    </Box>
                )}

                {claimContradictions.length > 0 && (
                    <Box data-testid="claim-details-contradicting">
                        <Typography variant="subtitle1" sx={{mb: 1, fontWeight: 600}}>
                            Contradicting Evidence ({claimContradictions.length})
                        </Typography>
                        {renderContradictionEvidenceGroups(contradictionEvidenceGroups)}
                    </Box>
                )}

                {/* Supporting Evidence Section — added references that prove this claim */}
                <Box data-testid="claim-details-supporting">
                    <Typography variant="subtitle1" sx={{mb: 1, fontWeight: 600}}>
                        Supporting Evidence ({isLoadingSupportingEvidence ? '…' : supportingEvidenceCount})
                    </Typography>
                    {isLoadingSupportingEvidence ? (
                        <Paper variant="outlined" sx={{p: 3, textAlign: 'center'}}>
                            <Typography color="text.secondary">Loading supporting evidence…</Typography>
                        </Paper>
                    ) : supportingEvidenceRows.length > 0 ? (
                        renderEvidenceList(supportingEvidenceRows, 'supporting')
                    ) : (<Paper variant="outlined" sx={{p: 3, textAlign: 'center'}}>
                        <Typography color="text.secondary">⚠️ No supporting evidence found</Typography>
                        <Typography variant="body2" color="text.secondary">Click below to search for
                            more</Typography>
                    </Paper>)}
                </Box>

                {/* Suggested Evidence Section — search hits not yet added */}
                <Box data-testid="claim-details-suggested">
                    <Typography variant="subtitle1" sx={{mb: 1, fontWeight: 600}}>
                        Suggested Evidence ({insightsLoading ? '…' : suggestedEvidenceCount})
                    </Typography>
                    {insightsLoading && suggestedEvidenceRows.length === 0 ? (
                        <Paper variant="outlined" sx={{p: 2, display: 'flex', alignItems: 'center', gap: 1}}>
                            <CircularProgress size={14} thickness={4} />
                            <Typography color="text.secondary" variant="body2">
                                Finding suggested evidence...
                            </Typography>
                        </Paper>
                    ) : suggestedEvidenceRows.length > 0 ? (
                        renderEvidenceList(suggestedEvidenceRows, 'suggested')
                    ) : (
                        <Paper variant="outlined" sx={{p: 3, textAlign: 'center'}}>
                            <Typography color="text.secondary">No suggested evidence yet</Typography>
                        </Paper>
                    )}
                </Box>

                {/* Contradicting Evidence Section */}
                {/*<Box data-testid="claim-details-contradicting">*/}
                {/*    <Typography variant="subtitle1" sx={{mb: 1, fontWeight: 600}}>*/}
                {/*        Contradicting Evidence ({claim.contradicting_evidence?.length || 0})*/}
                {/*    </Typography>*/}
                {/*    {claim.contradicting_evidence?.length > 0 ? (*/}
                {/*        <Paper variant="outlined">*/}
                {/*            <List disablePadding>*/}
                {/*                {claim.contradicting_evidence.map((evidence, idx) => {*/}
                {/*                    const referenceId = evidence?.referenceId;*/}
                {/*                    const reference = getReference(referenceId);*/}
                {/*                    const label = getReferenceLabel(reference, referenceId);*/}

                {/*                    return (*/}
                {/*                        <React.Fragment key={`${claim.id}-contra-${referenceId || idx}`}>*/}
                {/*                            {idx > 0 && <Divider/>}*/}
                {/*                            <ListItem*/}
                {/*                                disablePadding*/}
                {/*                                data-testid={`claim-details-contradicting-item-${idx}`}*/}
                {/*                                secondaryAction={*/}
                {/*                                    <Button*/}
                {/*                                        size="small"*/}
                {/*                                        endIcon={<OpenInNewIcon/>}*/}
                {/*                                        onClick={(e) => handleViewReference(e, referenceId)}*/}
                {/*                                        disabled={!reference?.docId}*/}
                {/*                                        data-testid={`claim-details-contradicting-view-${idx}`}*/}
                {/*                                    >*/}
                {/*                                        View*/}
                {/*                                    </Button>*/}
                {/*                                }*/}
                {/*                            >*/}
                {/*                                <ListItemButton*/}
                {/*                                    onClick={(e) => handleViewReference(e, referenceId)}*/}
                {/*                                    disabled={!reference?.docId}*/}
                {/*                                >*/}
                {/*                                    <ListItemText*/}
                {/*                                        primary={*/}
                {/*                                            <Stack direction="row" spacing={1} alignItems="center">*/}
                {/*                                                <Chip*/}
                {/*                                                    label={referenceId ? `Ref` : `#${idx + 1}`}*/}
                {/*                                                    size="small"*/}
                {/*                                                    variant="outlined"*/}
                {/*                                                />*/}
                {/*                                                <Typography variant="body2" color="text.secondary">*/}
                {/*                                                    {label}*/}
                {/*                                                </Typography>*/}
                {/*                                            </Stack>*/}
                {/*                                        }*/}
                {/*                                        secondary={*/}
                {/*                                            <Typography variant="body2"*/}
                {/*                                                        sx={{fontStyle: 'italic', mt: 1}}>*/}
                {/*                                                "{evidence?.text || ''}"*/}
                {/*                                            </Typography>*/}
                {/*                                        }*/}
                {/*                                    />*/}
                {/*                                </ListItemButton>*/}
                {/*                            </ListItem>*/}
                {/*                        </React.Fragment>*/}
                {/*                    );*/}
                {/*                })}*/}
                {/*            </List>*/}
                {/*        </Paper>*/}
                {/*    ) : (*/}
                {/*        <Paper variant="outlined" sx={{p: 3, textAlign: 'center'}}>*/}
                {/*            <Typography color="text.secondary">✓ No contradictions detected</Typography>*/}
                {/*        </Paper>*/}
                {/*    )}*/}
                {/*</Box>*/}

                {/* Keywords Search Query */}
                {!!claim.keywords_query && (<Box data-testid="claim-details-query">
                    <Typography variant="subtitle1" sx={{mb: 1, fontWeight: 600}}>
                        Keywords Search Query
                    </Typography>
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 2, bgcolor: 'grey.50', fontFamily: 'monospace', fontSize: '0.875rem',
                        }}
                    >
                        {normalizeKeywordsQuery(claim.keywords_query)}
                    </Paper>
                </Box>)}
            </Stack>
            )}
        </DialogContent>

        {/* Actions */}
        {claim && (
        <DialogActions>
            <Button
                variant="contained"
                onClick={handleSearchForMore}
                startIcon={<SearchIcon/>}
                data-testid="claim-details-search-more"
            >
                Search for More Evidence
            </Button>
        </DialogActions>
        )}
    </Dialog>);
}
